import React from 'react';
import { ShoppingBag, Lock, Globe } from 'lucide-react';
import { useShop } from '../context/ShopContext';

export const Header: React.FC = () => {
  const { locale, setLocale, currency, setCurrency, t, cartCount, setIsCartOpen, shippingConfig } = useShop();

  const showFreeShippingBanner = Boolean(shippingConfig?.free_shipping_enabled);
  const freeThreshold = currency === 'CHF'
    ? shippingConfig?.free_shipping_threshold_chf
    : shippingConfig?.free_shipping_threshold_eur;

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm transition-all">
      {/* Optional honest top banner - only if configured */}
      {showFreeShippingBanner && freeThreshold && (
        <div className="bg-neutral-900 text-white text-xs font-medium py-1.5 px-4 text-center">
          {locale === 'de'
            ? `Kostenloser Versand ab ${freeThreshold.toFixed(2)} ${currency}`
            : `Livraison offerte dès ${freeThreshold.toFixed(2)} ${currency} d'achats`}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Brand */}
        <div className="flex items-center space-x-3">
          <a href="#/" className="flex flex-col group">
            <span className="font-display text-xl font-bold tracking-tight text-gray-900 group-hover:text-pink-600 transition-colors">
              {t.nav.brand}
            </span>
            <span className="text-[10px] text-gray-500 font-medium hidden sm:inline-block">
              {t.nav.tagline}
            </span>
          </a>
        </div>

        {/* Right side controls */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Currency Switcher */}
          <div className="flex items-center bg-gray-100 p-0.5 rounded-lg text-xs font-medium">
            <button
              onClick={() => setCurrency('CHF')}
              className={`px-2 py-1 rounded-md transition-all ${
                currency === 'CHF' ? 'bg-white text-gray-900 shadow-sm font-semibold' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              CHF
            </button>
            <button
              onClick={() => setCurrency('EUR')}
              className={`px-2 py-1 rounded-md transition-all ${
                currency === 'EUR' ? 'bg-white text-gray-900 shadow-sm font-semibold' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              EUR
            </button>
          </div>

          {/* Language Switcher */}
          <div className="flex items-center bg-gray-100 p-0.5 rounded-lg text-xs font-medium">
            <button
              onClick={() => setLocale('fr')}
              className={`px-2 py-1 rounded-md transition-all ${
                locale === 'fr' ? 'bg-white text-gray-900 shadow-sm font-semibold' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              FR
            </button>
            <button
              onClick={() => setLocale('de')}
              className={`px-2 py-1 rounded-md transition-all ${
                locale === 'de' ? 'bg-white text-gray-900 shadow-sm font-semibold' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              DE
            </button>
          </div>

          {/* Admin link */}
          <a
            href="#/admin"
            title="Dashboard Administrateur"
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Lock className="w-4 h-4" />
          </a>

          {/* Cart button */}
          <button
            onClick={() => setIsCartOpen(true)}
            className="relative p-2 text-gray-800 hover:bg-pink-50 hover:text-pink-600 rounded-xl transition-all flex items-center gap-2 border border-gray-200"
            aria-label={t.nav.cart}
          >
            <ShoppingBag className="w-5 h-5 text-gray-900" />
            <span className="hidden sm:inline-block text-xs font-semibold">{t.nav.cart}</span>
            {cartCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[11px] font-bold text-white bg-pink-500 rounded-full animate-scale-in">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};
